"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Customer } from "../services/customer-service";
import OrderService, { OrderListItem ,ORDER_STATUS_LABELS} from "../../order-list/services/order-service";
import UserService, { UserStatusCode } from "../../user/services/user-service";
interface Props {
  name: string;
  data?: Customer;
  saleName?: string;
  loading?: boolean;
  onClose: () => void;
}

const CustomerDetailModal: React.FC<Props> = ({ name, data, saleName, loading, onClose }) => {
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loadingOrders, setLoadingOrders] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [resolvedSaleName, setResolvedSaleName] = useState<string>("");
  const { fetch: fetchOrders } = OrderService.list();
  const { fetch: fetchUsers } = UserService.list();

  useEffect(() => {
    if (saleName) {
      setResolvedSaleName(saleName);
      return;
    }
    if (data?.sale_id) {
      void (async () => {
        try {
          const res = await fetchUsers({ status: UserStatusCode.ACTIVE, limit: 500 });
          if (res?.data?.list) {
            const found = res.data.list.find((u: any) => Number(u.id) === Number(data.sale_id));
            if (found) setResolvedSaleName(found.name);
          }
        } catch {
          // ignore
        }
      })();
    } else {
      setResolvedSaleName("");
    }
  }, [saleName, data?.sale_id]);

  const formatCurrency = (val: unknown) => (Number(val) || 0).toLocaleString("vi-VN");
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!data?.id) return;
      setLoadingOrders(true);

      const res = await fetchOrders({ page, limit, customer_id: Number(data.id) });
      setLoadingOrders(false);
      if (!mounted || !res || res.status !== 200) return;

      const payload: any = res.data ?? {};
      const list: any[] = Array.isArray(payload.list)
        ? payload.list
        : Array.isArray(payload.items)
        ? payload.items
        : Array.isArray(payload)
        ? payload
        : [];

      // Nếu API chưa hỗ trợ filter theo customer_id → FE tự lọc
      const filtered = data?.id
        ? list.filter((x) => Number(x.customer_id) === Number(data.id))
        : list;

      // Xử lý phân trang (FE tự cắt nếu backend không làm)
      const start = (page - 1) * limit;
      const paginated = filtered.slice(start, start + limit);

      setOrders(paginated);
      setTotal(filtered.length);
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [data?.id, page, limit]);

  // Reset page khi đổi customer
  useEffect(() => {
    setPage(1);
  }, [data?.id]);

  return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-xl rounded-xl bg-white shadow-xl overflow-hidden">
        {/* HEADER */}
        <div className="flex justify-between items-center border-b px-4 py-3">
          <h2 className="text-base font-semibold text-slate-700">Thông tin khách hàng</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8 text-slate-400">
              Đang tải dữ liệu...
            </div>
          ) : !data ? (
            <div className="flex justify-center py-8 text-slate-400 italic">
              Không có dữ liệu khách hàng.
            </div>
          ) : (
            <div className="space-y-3 text-sm text-slate-700">
              {/* Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-slate-500 font-medium text-xs">Mã khách hàng</p>
                  <p className="mt-1 font-semibold text-slate-800 font-mono">
                    {data.code || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 font-medium text-xs">Tên khách hàng</p>
                  <p className="mt-1 font-semibold">{data.name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-slate-500 font-medium text-xs">Số điện thoại</p>
                  <p className="mt-1">{data.phone}</p>
                </div>
                <div>
                  <p className="text-slate-500 font-medium text-xs">NV Sale phụ trách</p>
                  <p className="mt-1 font-semibold text-blue-700">{resolvedSaleName || saleName || (data as any).sale?.name || (data as any).saleName || "—"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-slate-500 font-medium text-xs">Địa chỉ</p>
                  <p className="mt-1">{data.address || "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500 font-medium text-xs">Email</p>
                  <p className="mt-1">{data.email || "—"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-slate-500 font-medium text-xs">Trạng thái</p>
                  <p className="mt-1">
                    {data.status === 1 ? (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        Hoạt động
                      </span>
                    ) : (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                        Ngưng hoạt động
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 font-medium text-xs">Mã công ty</p>
                  <p className="mt-1">{data.company_id}</p>
                </div>
              </div>
            </div>
          )}

          {/* Orders */}
          <div className="pt-2 border-t">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Đơn hàng của khách</h3>

            <div className="overflow-x-auto border rounded-lg max-h-72 overflow-y-auto">
              <table className="w-full text-sm text-slate-700">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-2 text-left">STT</th>
                    <th className="px-2 py-2 text-left">Mã đơn</th>
                    <th className="px-2 py-2 text-left">Chi nhánh</th>
                    <th className="px-2 py-2 text-right">Thành tiền</th>
                    <th className="px-2 py-2 text-center">Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingOrders ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-3 text-center text-slate-500">
                        Đang tải...
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-3 text-center text-slate-500">
                        Không có đơn hàng
                      </td>
                    </tr>
                  ) : (
                    orders.map((o, idx) => {
                      const statusKey = Number((o as any).status ?? "");
                      return (
                        <tr key={(o as any).id ?? idx} className="border-t">
                          <td className="px-2 py-1">{(page - 1) * limit + idx + 1}</td>
                          <td className="px-2 py-1 font-medium">
                            {(o as any).code ?? (o as any).order_code}
                          </td>
                          <td className="px-2 py-1">{(o as any).branch?.name ?? "—"}</td>
                          <td className="px-2 py-1 text-right">
                            {formatCurrency((o as any).total_amount)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {ORDER_STATUS_LABELS[statusKey] ?? "Không xác định"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center text-xs text-slate-500 mt-2">
              <div>
                {total > 0
                  ? `Hiển thị ${(page - 1) * limit + 1}-${(page - 1) * limit + orders.length} / ${total}`
                  : "Không có dữ liệu"}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={limit}
                  onChange={(e) => {
                    setPage(1);
                    setLimit(Number(e.target.value));
                  }}
                  className="border rounded px-2 py-1"
                >
                  {[10, 20, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}/trang
                    </option>
                  ))}
                </select>
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Trước
                </button>
                <span>{page}</span>
                <button
                  disabled={(page - 1) * limit + orders.length >= total}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Sau
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end border-t px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-2 text-slate-600 hover:bg-slate-100"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailModal;
